import type {
  MonitoringStorageCategory,
  MonitoringStorageUsageRow,
} from "../../../shared/monitoringWorkflowsReport";
import { EXECUTION_RETENTION_SECONDS_PER_DAY } from "../../../shared/executionRetention";
import { formatBytes } from "../../lib/formatBytes";
import type { StatusSegment } from "../StatusBreakdownChart";
import { splitBySharePrefix } from "./foldSegments";
import { SPACE_COLORS } from "./monitoringPanelLayout";
import { OTHER_SPACES_KEY } from "./spaceSegments";

type Owner = "live" | "workflow" | "outside";
type StorageDimension =
  | "space"
  | "owner"
  | "workflow"
  | "kind"
  | "branch"
  | "volume";
type StorageView = StorageDimension | "files";
type Tile = { key: string; label: string; color?: string; note?: string };
// The bucket a row’s space is drawn under: its space, or the folded personal
// bucket.
export type SpaceTile = (spaceId: string) => Tile & { color: string };

const NONE_KEY = "__none__";
const FOLD = { minShare: 0.04, maxKept: 11 };

type Tone = { color: string; fill: string; ring: string };

const rampOf = (color: string) => /^var\(--([a-z]+)-\d+\)$/.exec(color)?.[1];

const GRAY_TONE: Tone = {
  color: "var(--gray-600)",
  fill: "var(--gray-200)",
  ring: "var(--gray-600)",
};

// A tile fills with its ramp’s light tints and marks itself with a stronger
// step, both theme-aware, rather than fading one color into the surface.
function topTone(color: string): Tone {
  const ramp = rampOf(color);
  return ramp
    ? { color, fill: `var(--${ramp}-400)`, ring: `var(--${ramp}-600)` }
    : { color, fill: color, ring: color };
}

// Below the top level, tiles step through their space’s ramp, strongest for the
// largest, so a drill reads as one space all the way down.
function drillTone(spaceColor: string, rank: number, shown: number): Tone {
  const ramp = rampOf(spaceColor);
  if (!ramp) return topTone(spaceColor);
  const lighter =
    shown <= 1
      ? 0
      : Math.round((Math.min(rank, shown - 1) / (shown - 1)) * 100);
  return {
    color: `color-mix(in oklab, var(--${ramp}-800), var(--${ramp}-600) ${lighter}%)`,
    fill: `color-mix(in oklab, var(--${ramp}-400), var(--${ramp}-200) ${lighter}%)`,
    ring: `var(--${ramp}-600)`,
  };
}

const OUTSIDE_NOTE =
  "Not tied to a workflow, such as personal chat volumes, code your chats run, or older data stored before runs were labeled by workflow.";

const OWNERS: Record<Owner, Tile> = {
  live: { key: "live", label: "Live volumes", color: "var(--purple-600)" },
  workflow: {
    key: "workflow",
    label: "Workflow data",
    color: "var(--sky-600)",
  },
  outside: {
    key: "outside",
    label: "Outside workflows",
    color: "var(--gray-600)",
    note: OUTSIDE_NOTE,
  },
};

// Live volumes belong to the space and are shared across its workflows;
// everything labeled with a workflow belongs to it.
function ownerOf(row: MonitoringStorageUsageRow): Owner {
  if (row.category === "live_volume") return "live";
  if (row.category === "personal_volume" || row.workflowId === null) {
    return "outside";
  }
  return "workflow";
}

const CATEGORIES: Record<MonitoringStorageCategory, Tile> = {
  live_volume: {
    key: "live_volume",
    label: "Live",
    color: "var(--purple-600)",
  },
  branch_volume: {
    key: "branch_volume",
    label: "Draft branches",
    color: "var(--orange-600)",
  },
  step_test_volume: {
    key: "step_test_volume",
    label: "Step-test copies",
    color: "var(--pink-600)",
  },
  run_volume: {
    key: "run_volume",
    label: "Run volumes",
    color: "var(--teal-600)",
  },
  personal_volume: {
    key: "personal_volume",
    label: "Chat volumes",
    color: "var(--yellow-600)",
  },
  execution: {
    key: "execution",
    label: "Execution data",
    color: "var(--sky-600)",
  },
  image: { key: "image", label: "Images", color: "var(--green-600)" },
};

// The single drill path to a row, so every piece of storage is reached one
// way. A branch split needs a workflow first, since branch names repeat
// across workflows.
function pathOf(row: MonitoringStorageUsageRow): StorageDimension[] {
  const owner = ownerOf(row);
  if (owner === "live") return ["space", "owner", "volume"];
  const head: StorageDimension[] =
    owner === "workflow"
      ? ["space", "owner", "workflow", "kind"]
      : ["space", "owner", "kind"];
  switch (row.category) {
    case "execution":
    case "image":
      return head;
    case "branch_volume":
    case "step_test_volume":
      return owner === "workflow"
        ? [...head, "branch", "volume"]
        : [...head, "volume"];
    default:
      return [...head, "volume"];
  }
}

// Rows narrowed by the same keys share a path up to their depth.
function dimensionAt(
  rows: readonly MonitoringStorageUsageRow[],
  depth: number
): StorageDimension | null {
  return rows[0] ? (pathOf(rows[0])[depth] ?? null) : null;
}

function tileFor(
  dimension: StorageDimension,
  row: MonitoringStorageUsageRow,
  spaceTile: SpaceTile
): Tile {
  switch (dimension) {
    case "space":
      return spaceTile(row.spaceId);
    case "owner":
      return OWNERS[ownerOf(row)];
    case "kind":
      return CATEGORIES[row.category];
    case "workflow":
      return row.workflowId === null
        ? { key: NONE_KEY, label: "Outside workflows", note: OUTSIDE_NOTE }
        : {
            key: row.workflowId,
            label: row.workflowName ?? "Deleted workflow",
          };
    case "branch":
      return row.branchId === null
        ? { key: NONE_KEY, label: "No branch" }
        : { key: row.branchId, label: row.branchName ?? "Deleted branch" };
    case "volume":
      return row.volumeId === null
        ? { key: NONE_KEY, label: "Unknown volume" }
        : { key: row.volumeId, label: row.volumeName ?? "Deleted volume" };
  }
}

type VolumeTargetBase = {
  spaceId: string;
  volumeId: string;
  volumeName: string;
  branchId: string | null;
  branchName: string | null;
};

export type StorageFileTarget = VolumeTargetBase & {
  category: "live_volume" | "branch_volume" | "personal_volume";
};

export type StorageVolumeTarget =
  | StorageFileTarget
  | (VolumeTargetBase & {
      category: "run_volume" | "step_test_volume";
      workflowId: string;
    });

const BROWSABLE = new Set<MonitoringStorageCategory>([
  "live_volume",
  "branch_volume",
  "personal_volume",
]);

// Run and step-test copies span many refs, so they're addressed by workflow
// and volume rather than listed.
const RUN_COPIES = new Set<MonitoringStorageCategory>([
  "run_volume",
  "step_test_volume",
]);

// Rows that are all one volume of one kind, on one branch, can be deleted
// whole.
function volumeTarget(
  rows: readonly MonitoringStorageUsageRow[]
): StorageVolumeTarget | null {
  const [first] = rows;
  if (
    !first ||
    first.volumeId === null ||
    first.volumeName === null ||
    !(BROWSABLE.has(first.category) || RUN_COPIES.has(first.category))
  ) {
    return null;
  }
  const runCopy = RUN_COPIES.has(first.category);
  // A deleted workflow’s run copies can’t be resolved for deletion.
  if (runCopy && (first.workflowId === null || first.workflowName === null)) {
    return null;
  }
  const sameRef = rows.every(
    (row) =>
      row.category === first.category &&
      row.spaceId === first.spaceId &&
      row.volumeId === first.volumeId &&
      row.branchId === first.branchId &&
      (!runCopy || row.workflowId === first.workflowId)
  );
  if (!sameRef) return null;
  const base = {
    spaceId: first.spaceId,
    volumeId: first.volumeId,
    volumeName: first.volumeName,
    branchId: first.branchId,
    branchName: first.branchName,
  };
  return (
    runCopy
      ? { ...base, category: first.category, workflowId: first.workflowId }
      : { ...base, category: first.category }
  ) as StorageVolumeTarget;
}

// Files are listed from a single ref of a browsable kind.
function fileTarget(
  rows: readonly MonitoringStorageUsageRow[]
): StorageFileTarget | null {
  const target = volumeTarget(rows);
  return target && BROWSABLE.has(target.category)
    ? (target as StorageFileTarget)
    : null;
}

export function parseStoragePath(param: string | undefined): string[] {
  return param ? param.split("/") : [];
}

export function storagePath(keys: readonly string[]): string {
  return keys.join("/");
}

export type StorageWorkflowLink = {
  spaceId: string;
  workflowId: string;
  branchId: string | null;
};

type StorageUsageTile = {
  key: string;
  label: string;
  color: string;
  fill: string;
  ring: string;
  bytes: number;
  drillable: boolean;
  workflow: StorageWorkflowLink | null;
  volume: StorageVolumeTarget | null;
  note?: string;
};

// Workflow and branch tiles point at the workflow, on the branch for a draft;
// a deleted workflow or branch has nowhere to go.
function workflowLink(
  dimension: StorageDimension,
  [row]: readonly MonitoringStorageUsageRow[]
): StorageWorkflowLink | null {
  if (!row?.workflowId || row.workflowName === null) return null;
  const link = { spaceId: row.spaceId, workflowId: row.workflowId };
  switch (dimension) {
    case "workflow":
      return { ...link, branchId: null };
    case "branch":
      return row.branchId && row.branchName !== null
        ? { ...link, branchId: row.branchId }
        : null;
    case "space":
    case "owner":
    case "kind":
    case "volume":
      return null;
  }
}

export type StorageUsageLevel = {
  crumbs: { keys: string[]; label: string }[];
  // Null at a leaf, where nothing is left to split by or browse.
  view: StorageView | null;
  files: StorageFileTarget | null;
  tiles: StorageUsageTile[];
  segments: StatusSegment[];
  totalBytes: number;
};

// Keys that no longer match any row (a stale URL) cut the drill short there.
export function storageUsageLevel(
  rows: readonly MonitoringStorageUsageRow[],
  keys: readonly string[],
  spaceTile: SpaceTile
): StorageUsageLevel {
  const crumbs: StorageUsageLevel["crumbs"] = [];
  let spaceColor: string | undefined;
  let remaining = rows;
  for (const [depth, key] of keys.entries()) {
    const dimension = dimensionAt(remaining, depth);
    if (dimension === null) break;
    const matched = remaining.filter(
      (row) => tileFor(dimension, row, spaceTile).key === key
    );
    if (matched.length === 0) break;
    remaining = matched;
    const tile = tileFor(dimension, matched[0]!, spaceTile);
    spaceColor ??= tile.color;
    crumbs.push({ keys: keys.slice(0, depth + 1), label: tile.label });
  }

  const files = fileTarget(remaining);
  const view: StorageView | null =
    dimensionAt(remaining, crumbs.length) ?? (files ? "files" : null);
  const totalBytes = remaining.reduce((sum, row) => sum + row.liveBytes, 0);
  const level = {
    crumbs,
    view,
    files,
    totalBytes,
  };
  if (view === null || view === "files") {
    return { ...level, tiles: [], segments: [] };
  }

  const grouped = new Map<
    string,
    Tile & { bytes: number; rows: MonitoringStorageUsageRow[] }
  >();
  for (const row of remaining) {
    const tile = tileFor(view, row, spaceTile);
    const existing = grouped.get(tile.key);
    if (existing) {
      existing.bytes += row.liveBytes;
      existing.rows.push(row);
    } else {
      grouped.set(tile.key, { ...tile, bytes: row.liveBytes, rows: [row] });
    }
  }
  const sorted = [...grouped.values()].sort(
    (a, b) => b.bytes - a.bytes || a.label.localeCompare(b.label)
  );
  const shown = splitBySharePrefix(
    sorted,
    (tile) => tile.bytes,
    totalBytes,
    FOLD
  ).kept.length;
  const tiles: StorageUsageTile[] = sorted.map((tile, i) => {
    const workflow = workflowLink(view, tile.rows);
    const tone =
      tile.key === NONE_KEY
        ? GRAY_TONE
        : spaceColor
          ? drillTone(spaceColor, i, shown)
          : topTone(tile.color ?? SPACE_COLORS[i % SPACE_COLORS.length]!);
    return {
      key: tile.key,
      label: tile.label,
      bytes: tile.bytes,
      ...tone,
      drillable:
        fileTarget(tile.rows) !== null ||
        dimensionAt(tile.rows, crumbs.length + 1) !== null,
      workflow,
      volume: view === "volume" ? volumeTarget(tile.rows) : null,
      note: tile.note,
    };
  });

  return {
    ...level,
    tiles,
    segments: foldSegments(tiles, totalBytes),
  };
}

function foldSegments(
  tiles: readonly StorageUsageTile[],
  totalBytes: number
): StatusSegment[] {
  const { kept, folded } = splitBySharePrefix(
    tiles,
    (tile) => tile.bytes,
    totalBytes,
    FOLD
  );
  const segments: StatusSegment[] = kept.map((tile) => ({
    key: tile.key,
    count: tile.bytes,
    label: tile.label,
    color: tile.color,
    fill: tile.fill,
    ring: tile.ring,
    note: tile.note,
  }));
  if (folded.length > 0) {
    segments.push({
      key: OTHER_SPACES_KEY,
      count: folded.reduce((sum, tile) => sum + tile.bytes, 0),
      ...GRAY_TONE,
      label: `${folded.length} more`,
      details: folded.map((tile) => ({
        label: tile.label,
        value: formatBytes(tile.bytes),
      })),
    });
  }
  return segments;
}

export function formatRetention(seconds: number): string {
  const days = seconds / EXECUTION_RETENTION_SECONDS_PER_DAY;
  if (Number.isInteger(days)) return days === 1 ? "1 day" : `${days} days`;
  const hours = Math.round(seconds / 3600);
  return hours === 1 ? "1 hour" : `${hours} hours`;
}
